import { useState } from 'react'
import { useAddDefaultKeywordsMutation } from '../../api/client'
import SimpleLoader from '../SimpleLoader'
import Modal from '../Modal'
import Button from '../Button'

type Props = {
    accountId: number
    onChangeKeywords: () => void
}
function AddDefaultKeywordsButton(props: Props) {
    const { accountId, onChangeKeywords } = props

    const [confirmOpen, setConfirmOpen] = useState(false)

    const [addDefaultKeywordsMutation, { loading }] = useAddDefaultKeywordsMutation()

    async function handleResetToDefault() {
        await addDefaultKeywordsMutation({
            variables: { accountId: accountId, reset: true }
        })

        setConfirmOpen(false)
        await onChangeKeywords()
    }

    async function handleAddDefaults() {
        await addDefaultKeywordsMutation({
            variables: { accountId: accountId, reset: false }
        })
        setConfirmOpen(false)
        await onChangeKeywords()
    }

    return (
        <div>
            <Button
                text="Add default keywords... (admin only)"
                primary={false}
                onClick={() => setConfirmOpen(true)}
            ></Button>

            <Modal
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                title="Add default keywords"
                description="Choose how you'd like to handle default keywords for this account."
                size="lg"
                footer={
                    <Button
                        text="Cancel"
                        primary={false}
                        onClick={() => setConfirmOpen(false)}
                        disabled={loading}
                    />
                }
            >
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Option 1: Reset and Add */}
                        <div className="border border-ink-200 rounded-lg p-6 hover:border-ink-300 transition-colors">
                            <div className="mb-4">
                                <h4 className="font-sans !font-medium text-lg text-ink-900 mb-2">
                                    Replace all keywords
                                </h4>
                                <p className="text-sm text-ink-500 leading-relaxed">
                                    Delete all existing keywords and replace them with only the
                                    default set. This will remove any custom keywords that have
                                    been added previously.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="w-full inline-flex justify-center rounded-md border border-transparent bg-red-100 px-4 py-3 text-sm font-medium text-red-900 hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 transition-colors"
                                onClick={handleResetToDefault}
                                disabled={loading}
                            >
                                Replace all keywords
                            </button>
                        </div>

                        {/* Option 2: Add to Existing */}
                        <div className="border border-ink-200 rounded-lg p-6 hover:border-ink-300 transition-colors">
                            <div className="mb-4">
                                <h4 className="font-sans !font-medium text-lg text-ink-900 mb-2">
                                    Add to existing
                                </h4>
                                <p className="text-sm text-ink-500 leading-relaxed">
                                    Add the default keywords to your current list. This will
                                    preserve all existing keywords and add any defaults that
                                    aren't already present.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="w-full inline-flex justify-center rounded-md border border-transparent bg-arwen-100 px-4 py-3 text-sm font-medium text-arwen-900 hover:bg-arwen-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-arwen-500 focus-visible:ring-offset-2 transition-colors"
                                onClick={handleAddDefaults}
                                disabled={loading}
                            >
                                Add to existing
                            </button>
                        </div>
                    </div>

                    {/* Loading indicator */}
                    {loading && (
                        <div className="flex justify-center py-4">
                            <SimpleLoader loading={loading} />
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    )
}

export default AddDefaultKeywordsButton
